import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BarChart, StackedBarChart, fmtNum } from './analytics-shared.jsx';

const LIMIT = 500;
const number = value => Math.max(0, Number(value) || 0);
const series = [
  { key: 'consumerErrorCalls', label: '消费者侧', color: 'var(--fg-4)' },
  { key: 'failedCalls', label: '失败', color: 'var(--c-rose)' },
  { key: 'successCalls', label: '成功', color: 'var(--c-mint)' },
];

/** Every bucket stays in the SVG path and hit-test population. No sampling,
 * aggregation, percentile averaging or replacement of the backend statistics. */
function DenseBars({ data, fields, height, maximum, label, onBarClick, legend = [] }) {
  const host = useRef(null);
  const [width, setWidth] = useState(800);
  const [active, setActive] = useState(null);
  useEffect(() => {
    const measure = () => { const w = Math.floor(host.current?.getBoundingClientRect().width || 0); if (w > 80) setWidth(w); };
    measure();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(measure); observer.observe(host.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => setActive(null), [data]);
  const left = 40, right = 8, top = legend.length ? 20 : 6, bottom = 23;
  const plotWidth = width - left - right, plotHeight = Math.max(1, height - top - bottom);
  const paths = useMemo(() => {
    const result = fields.map(() => '');
    const cell = plotWidth / data.length, bar = Math.max(0.02, cell * 0.82);
    for (let i = 0; i < data.length; i++) {
      let base = 0;
      fields.forEach((field, j) => {
        const v = number(data[i][field.key]);
        if (v) {
          const x = left + i * cell + (cell - bar) / 2;
          const y = top + plotHeight * (1 - (base + v) / maximum);
          const h = plotHeight * v / maximum;
          result[j] += `M${x.toFixed(2)},${y.toFixed(2)}h${bar.toFixed(2)}v${h.toFixed(2)}h-${bar.toFixed(2)}Z`;
        }
        base += v;
      });
    }
    return result;
  }, [data, fields, plotWidth, plotHeight, maximum, top]);
  const indexAt = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) * width / (rect.width || width);
    return x < left || x > width - right ? null : Math.min(data.length - 1, Math.floor((x - left) / plotWidth * data.length));
  };
  const choose = i => { if (i != null) onBarClick?.(data[i], i); };
  const point = active == null ? null : data[active];
  const timestamp = d => d?.ts ?? d?.date ?? '';
  const ticks = [...new Set([0, Math.floor((data.length - 1) / 2), data.length - 1])];
  return <div ref={host} style={{ position: 'relative', width: '100%', height }}>
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} role="application"
      aria-label={`${label}，左右箭头选择时段，回车下钻日志`} tabIndex={0}
      style={{ cursor: onBarClick ? 'pointer' : undefined, outlineOffset: 2 }}
      onMouseMove={e => setActive(indexAt(e))} onMouseLeave={() => setActive(null)}
      onClick={e => choose(indexAt(e))}
      onFocus={() => setActive(i => i ?? 0)} onBlur={() => setActive(null)}
      onKeyDown={e => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
          e.preventDefault(); setActive(i => e.key === 'Home' ? 0 : e.key === 'End' ? data.length - 1 : Math.max(0, Math.min(data.length - 1, (i ?? 0) + (e.key === 'ArrowRight' ? 1 : -1))));
        } else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(active ?? 0); }
        else if (e.key === 'Escape') setActive(null);
      }}>
      {[...new Set([0, Math.ceil(maximum / 2), maximum])].map(tick => <g key={tick}>
        <line x1={left} x2={width - right} y1={top + plotHeight * (1 - tick / maximum)} y2={top + plotHeight * (1 - tick / maximum)} stroke="var(--line)" strokeDasharray="2 4" />
        <text x={left - 5} y={top + plotHeight * (1 - tick / maximum) + 3} fill="var(--fg-4)" textAnchor="end" fontSize={10}>{fmtNum(tick)}</text>
      </g>)}
      {fields.map((field, i) => <path key={field.key} data-series={field.key} d={paths[i]} fill={field.color} />)}
      {active != null && <line x1={left + (active + 0.5) / data.length * plotWidth} x2={left + (active + 0.5) / data.length * plotWidth} y1={top} y2={top + plotHeight} stroke="var(--fg-2)" />}
      {ticks.map(i => <text key={i} x={left + (i + 0.5) / data.length * plotWidth} y={height - 5} textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'} fontSize={10} fill="var(--fg-4)">{String(timestamp(data[i])).slice(5, 16)}</text>)}
    </svg>
    {legend.length > 0 && <div style={{ position: 'absolute', top: 0, right: 4, display: 'flex', gap: 10, fontSize: 10, color: 'var(--fg-3)', pointerEvents: 'none' }}>
      {legend.map(field => <span key={field.key}><span style={{ display: 'inline-block', width: 10, height: 8, marginRight: 4, background: field.color }} />{field.label} {fmtNum(field.total)}</span>)}
    </div>}
    {point && <div role="status" style={{ position: 'absolute', right: 8, top: 22, padding: '6px 10px', background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 6, fontSize: 11, pointerEvents: 'none', color: 'var(--fg-1)' }}>
      <div style={{ color: 'var(--fg-3)', fontSize: 10 }}>{timestamp(point)}</div>
      {fields.map(field => <div key={field.key}>{field.label} {fmtNum(number(point[field.key]))}</div>)}
    </div>}
  </div>;
}

export function TrafficCallsChart(props) {
  const { data, valueKey = 'calls', height = 120 } = props;
  const maximum = data?.reduce((m, d) => Math.max(m, number(d[valueKey])), 0) || 0;
  if (!data || data.length <= LIMIT || !maximum) return <BarChart {...props} />;
  return <DenseBars {...props} maximum={maximum} height={height} fields={[{ key: valueKey, label: '调用', color: 'var(--c-mint)' }]} label="调用量时序" />;
}
export function TrafficStatusChart(props) {
  const { data, height = 160, show = 'all' } = props;
  const maximum = data?.reduce((m, d) => Math.max(m, number(d.successCalls) + number(d.failedCalls) + (show === 'all' ? number(d.consumerErrorCalls) : 0)), 0) || 0;
  if (!data || data.length <= LIMIT || !maximum) return <StackedBarChart {...props} />;
  const fields = series.filter(f => f.key === 'successCalls' ? show !== 'failed' : f.key === 'failedCalls' ? show !== 'success' : show === 'all');
  const totals = series.map(f => ({ ...f, total: data.reduce((n, d) => n + number(d[f.key]), 0) }));
  const legend = [totals[2], totals[1], ...(show === 'all' && totals[0].total > 0 ? [totals[0]] : [])];
  return <DenseBars {...props} maximum={maximum} height={height} fields={fields} legend={legend} label="成功失败时序" />;
}
